/**
 * QueryExecutionService - Handles all query execution, validation, and retry logic
 * Extracted from BaseLLM for better separation of concerns
 */

import { Component } from '../../types';
import { ensureQueryLimit, validateAndFixSqlQuery } from '../utils';
import { schema } from '../schema';
import { promptLoader } from '../prompt-loader';
import { LLM } from '../../llm';
import { logger } from '../../utils/logger';
import { queryCache } from '../../utils/query-cache';
import {
	MAX_QUERY_VALIDATION_RETRIES,
	MAX_TOKENS_QUERY_FIX,
	MAX_AGENT_QUERY_LIMIT
} from '../constants';

/**
 * Context for component when requesting query fix
 */
export interface ComponentContext {
	name: string;
	type: string;
	title?: string;
}

/**
 * Result of query validation
 */
export interface QueryValidationResult {
	component: Component | null;
	queryKey: string;
	result: any;
	validated: boolean;
}

/**
 * Result of batch query validation
 */
export interface BatchValidationResult {
	components: Component[];
	queryResults: Map<string, any>;
}

/**
 * Configuration for QueryExecutionService
 */
export interface QueryExecutionServiceConfig {
	defaultLimit: number;
	getModelForTask: (taskType: 'simple' | 'complex') => string;
	getApiKey: (apiKey?: string) => string | undefined;
	providerName: string;
}

/**
 * QueryExecutionService handles all query-related operations
 */
export class QueryExecutionService {
	private config: QueryExecutionServiceConfig;

	constructor(config: QueryExecutionServiceConfig) {
		this.config = config;
	}

	/**
	 * Get the cache key for a query
	 * This ensures the cache key matches what the frontend will send
	 */
	getQueryCacheKey(query: any): string {
		if (typeof query === 'string') {
			return query;
		} else if (query?.sql) {
			const values = query.values || query.params;
			if (values && Object.keys(values).length > 0) {
				return JSON.stringify({ sql: query.sql, values });
			} else {
				return query.sql;
			}
		}
		return '';
	}

	/**
	 * Execute a query against the database
	 * @param query - The SQL query to execute (string or object with sql/values)
	 * @param collections - Collections object containing database execute function
	 * @returns Object with result data and cache key
	 */
	async executeQuery(
		query: any,
		collections: any
	): Promise<{ result: any; cacheKey: string }> {
		const cacheKey = this.getQueryCacheKey(query);

		if (!cacheKey) {
			throw new Error('Invalid query format: expected string or object with sql property');
		}

		if (!collections?.['database']?.['execute']) {
			throw new Error('Database collection not registered. Please register database.execute collection to execute queries.');
		}

		const result = await collections['database']['execute']({ sql: cacheKey });
		return { result, cacheKey };
	}

	/**
	 * Request the LLM to fix a failed SQL query
	 * @param failedQuery - The query that failed execution
	 * @param errorMessage - The error message from the failed execution
	 * @param componentContext - Context about the component
	 * @param apiKey - Optional API key
	 * @returns Fixed query string
	 */
	async requestQueryFix(
		failedQuery: string,
		errorMessage: string,
		componentContext: ComponentContext,
		apiKey?: string
	): Promise<string> {
		const schemaDoc = schema.generateSchemaDocumentation();
		const databaseRules = await promptLoader.loadDatabaseRules();

		const prompt = `You are a SQL expert. Fix the following SQL query that failed execution.

## Database Schema
${schemaDoc}

## Database-Specific SQL Rules
${databaseRules}

## Component Context
- Component Name: ${componentContext.name}
- Component Type: ${componentContext.type}
- Title: ${componentContext.title || 'N/A'}

## Failed Query
\`\`\`sql
${failedQuery}
\`\`\`

## Error Message
${errorMessage}

## Instructions
1. Analyze the error message and identify what caused the query to fail
2. Fix the query to resolve the error while preserving the original intent
3. Ensure the fixed query follows the database-specific SQL rules above
4. Return ONLY the fixed SQL query, no explanations or markdown

Fixed SQL query:`;

		const response = await LLM.text(
			{
				sys: 'You are a SQL expert. Return only the fixed SQL query with no additional text, explanations, or markdown formatting.',
				user: prompt
			},
			{
				model: this.config.getModelForTask('simple'),
				maxTokens: MAX_TOKENS_QUERY_FIX,
				temperature: 0,
				apiKey: this.config.getApiKey(apiKey)
			}
		);

		// Clean up the response
		let fixedQuery = response.trim();
		fixedQuery = fixedQuery.replace(/^```sql\s*/i, '').replace(/\s*```$/i, '');
		fixedQuery = fixedQuery.replace(/^```\s*/i, '').replace(/\s*```$/i, '');

		// Apply static SQL fixes
		const { query: validatedQuery } = validateAndFixSqlQuery(fixedQuery);

		return validatedQuery;
	}

	/**
	 * Validate a single component's query with retry logic
	 * @param component - The component to validate
	 * @param collections - Collections object containing database execute function
	 * @param apiKey - Optional API key for LLM calls
	 * @returns Validation result with component, query key, and result
	 */
	async validateSingleQuery(
		component: Component,
		collections: any,
		apiKey?: string
	): Promise<QueryValidationResult> {
		const query = component.props?.query;
		const originalQueryKey = this.getQueryCacheKey(query);
		const queryStr = typeof query === 'string' ? query : (query as any)?.sql || '';
		let finalQueryKey = originalQueryKey;

		let currentQuery: string | { sql: string; values?: any; params?: any } =
			typeof query === 'string' ? query : { sql: (query as any)?.sql || '', values: (query as any)?.values, params: (query as any)?.params };
		let currentQueryStr = queryStr;
		let validated = false;
		let lastError = '';
		let result: any = null;
		let attempts = 0;

		logger.info(`[${this.config.providerName}] Validating query for component: ${component.name} (${component.type})`);

		while (attempts < MAX_QUERY_VALIDATION_RETRIES && !validated) {
			attempts++;

			try {
				logger.debug(`[${this.config.providerName}] Query validation attempt ${attempts}/${MAX_QUERY_VALIDATION_RETRIES} for ${component.name}`);
				const validationResult = await this.executeQuery(currentQuery, collections);
				result = validationResult.result;

				validated = true;
				queryCache.set(validationResult.cacheKey, result);

				logger.info(`[${this.config.providerName}] ✓ Query validated for ${component.name} (attempt ${attempts}) - cached for frontend`);

				// Update component with the fixed query if it was modified
				if (currentQueryStr !== queryStr) {
					const fixedQuery = typeof query === 'string' ? currentQueryStr : { ...(query as any), sql: currentQueryStr };
					component.props = {
						...component.props,
						query: fixedQuery
					};
					finalQueryKey = this.getQueryCacheKey(fixedQuery);
					logger.info(`[${this.config.providerName}] Updated ${component.name} with fixed query`);
				}

			} catch (error) {
				lastError = error instanceof Error ? error.message : String(error);
				logger.warn(`[${this.config.providerName}] Query validation failed for ${component.name} (attempt ${attempts}/${MAX_QUERY_VALIDATION_RETRIES}): ${lastError}`);

				if (attempts >= MAX_QUERY_VALIDATION_RETRIES) {
					logger.error(`[${this.config.providerName}] ✗ Max retries reached for ${component.name}, excluding from response`);
					break;
				}

				// Request LLM to fix the query
				logger.info(`[${this.config.providerName}] Requesting query fix from LLM for ${component.name}...`);

				try {
					const fixedQueryStr = await this.requestQueryFix(
						currentQueryStr,
						lastError,
						{
							name: component.name,
							type: component.type,
							title: component.props?.title
						},
						apiKey
					);

					if (fixedQueryStr && fixedQueryStr !== currentQueryStr) {
						logger.info(`[${this.config.providerName}] Received fixed query for ${component.name}, retrying...`);
						const limitedFixedQuery = ensureQueryLimit(fixedQueryStr, this.config.defaultLimit, MAX_AGENT_QUERY_LIMIT);
						currentQueryStr = limitedFixedQuery;
						if (typeof currentQuery === 'string') {
							currentQuery = limitedFixedQuery;
						} else {
							currentQuery = { ...currentQuery, sql: limitedFixedQuery };
						}
					} else {
						logger.warn(`[${this.config.providerName}] LLM returned same or empty query, stopping retries`);
						break;
					}
				} catch (fixError) {
					const fixErrorMsg = fixError instanceof Error ? fixError.message : String(fixError);
					logger.error(`[${this.config.providerName}] Failed to get query fix from LLM: ${fixErrorMsg}`);
					break;
				}
			}
		}

		if (!validated) {
			logger.warn(`[${this.config.providerName}] Component ${component.name} excluded from response due to failed query validation`);
		}

		return {
			component: validated ? component : null,
			queryKey: finalQueryKey,
			result,
			validated
		};
	}

	/**
	 * Validate multiple component queries in parallel
	 * @param components - Array of components with potential queries
	 * @param collections - Collections object containing database execute function
	 * @param apiKey - Optional API key for LLM calls
	 * @returns Object with validated components and query results map
	 */
	async validateComponentQueries(
		components: Component[],
		collections: any,
		apiKey?: string
	): Promise<BatchValidationResult> {
		const queryResults = new Map<string, any>();
		const validatedComponents: Component[] = [];

		// Separate components with and without queries
		const componentsWithoutQuery: Component[] = [];
		const componentsWithQuery: Component[] = [];

		for (const component of components) {
			if (!component.props?.query) {
				componentsWithoutQuery.push(component);
			} else {
				componentsWithQuery.push(component);
			}
		}

		// Components without queries are automatically valid
		validatedComponents.push(...componentsWithoutQuery);

		if (componentsWithQuery.length === 0) {
			return { components: validatedComponents, queryResults };
		}

		logger.info(`[${this.config.providerName}] Validating ${componentsWithQuery.length} component queries in parallel...`);

		// Execute all validations in parallel
		const validationPromises = componentsWithQuery.map(component =>
			this.validateSingleQuery(component, collections, apiKey)
		);

		const results = await Promise.allSettled(validationPromises);

		// Process results
		for (let i = 0; i < results.length; i++) {
			const result = results[i];
			const component = componentsWithQuery[i];

			if (result.status === 'fulfilled') {
				const { component: validatedComponent, queryKey, result: queryResult, validated } = result.value;

				if (validated && validatedComponent) {
					validatedComponents.push(validatedComponent);
					if (queryResult) {
						queryResults.set(queryKey, queryResult);
						queryResults.set(`${component.id}:${queryKey}`, queryResult);
					}
				}
			} else {
				logger.error(`[${this.config.providerName}] Unexpected error validating ${component.name}: ${result.reason}`);
			}
		}

		logger.info(`[${this.config.providerName}] Parallel validation complete: ${validatedComponents.length}/${components.length} components validated`);

		return {
			components: validatedComponents,
			queryResults
		};
	}
}
